import type { CompanyApprovalStatus } from '../contexts/AuthContext'
import { clearLegacyAuthStorage } from './legacyStorage'

export const ENTRY_CONTEXT_KEY = 'COMFORTHUB_ENTRY_CONTEXT'

export interface EntryContext {
  companyId?: string | null
  previewRole?: string | null
  isPlatformAdmin?: boolean
  isCourier?: boolean
  isCourierPartner?: boolean
}

export function getEntryContext(): EntryContext | null {
  try {
    const raw = sessionStorage.getItem(ENTRY_CONTEXT_KEY)
    if (!raw) return null
    return JSON.parse(raw) as EntryContext
  } catch {
    return null
  }
}

export function setEntryContext(ctx: EntryContext): void {
  sessionStorage.setItem(ENTRY_CONTEXT_KEY, JSON.stringify(ctx))
}

export function clearEntryContext(): void {
  sessionStorage.removeItem(ENTRY_CONTEXT_KEY)
}

export function hasEntryContext(): boolean {
  return getEntryContext() !== null
}

export function legacyRoleToLabel(role: string): string {
  if (role === 'OWNER') return 'Merchant'
  if (role === 'MANAGER') return 'Manager'
  if (role === 'WORKER') return 'Worker'
  return role
}

export function resolvePostEntryPath(options: {
  isPlatformAdmin: boolean
  companyApprovalStatus: CompanyApprovalStatus
  previewRole?: string | null
  isCourier?: boolean
  isCourierPartner?: boolean
}): string {
  const { isPlatformAdmin, companyApprovalStatus, previewRole, isCourier, isCourierPartner } = options

  // The server-backed platform-admin identity wins over a stale merchant preview.
  // Explicit company entry passes isPlatformAdmin=false so admins can still enter
  // a merchant company context intentionally.
  if (isPlatformAdmin) {
    return '/platform'
  }

  if (previewRole === 'Courier') {
    return '/courier/deliveries'
  }

  if (previewRole === 'Partner') {
    return '/partner/orders'
  }

  if (previewRole === 'Admin') {
    return '/platform'
  }

  // Courier-only users (no company membership) land on deliveries.
  if (isCourier && !previewRole && companyApprovalStatus === 'none') {
    return '/courier/deliveries'
  }

  if (isCourierPartner && !previewRole && companyApprovalStatus === 'none') {
    return '/partner/orders'
  }

  if (companyApprovalStatus === 'pending') return '/onboarding/pending'
  if (companyApprovalStatus === 'rejected') return '/onboarding/rejected'
  if (companyApprovalStatus === 'none') return '/onboarding/apply'

  return '/dashboard'
}

/** A persona's home page: Admin → platform, Courier → deliveries, Partner → orders, else the dashboard. */
export function personaHomePath(persona: string | null | undefined): string {
  if (persona === 'Admin') return '/platform'
  if (persona === 'Courier') return '/courier/deliveries'
  if (persona === 'Partner') return '/partner/orders'
  return '/dashboard'
}

const NEXT_BASE = 'https://backoffice.invalid'

/**
 * The page to return to after the entry picker, from a `next` value, or null.
 * Only a path on this site counts: it starts with a single `/`, and `/` and
 * `/entry` themselves are never targets. `//host`, `https://…` and the like are
 * ignored, so a link can't send anyone off the site.
 */
export function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return null
  let url: URL
  try {
    url = new URL(raw, NEXT_BASE)
  } catch {
    return null
  }
  // Check what the parser made of it: it drops tabs and newlines, reads `\` as `/`
  // and resolves dot segments, so `/\evil.example` or `/.//evil.example` would leave the site.
  if (url.origin !== NEXT_BASE || url.pathname.startsWith('//')) return null
  if (/^\/(entry\/?)?$/i.test(url.pathname)) return null
  return `${url.pathname}${url.search}${url.hash}`
}

/** The entry picker's address, carrying `target` as `next` when it is safe to return to. */
export function entryPathFor(target: string): string {
  const next = safeNextPath(target)
  return next ? `/entry?next=${encodeURIComponent(next)}` : '/entry'
}

/**
 * The Welcome page's address, carrying `target` as `next` when it is safe to
 * return to. A signed-out visitor is sent here, so the page the link asked for
 * survives the sign-in and can be handed back to the entry picker afterwards.
 */
export function welcomePathFor(target: string): string {
  const next = safeNextPath(target)
  return next ? `/?next=${encodeURIComponent(next)}` : '/'
}

/** The part of the app a path belongs to. Each persona works in one of them. */
function appAreaOf(path: string): string {
  const pathname = path.split(/[?#]/)[0]
  for (const area of ['platform', 'courier', 'partner']) {
    if (pathname === `/${area}` || pathname.startsWith(`/${area}/`)) return area
  }
  // /onboarding/apply, /pending and /rejected; the setup wizard at /onboarding is a company page.
  if (pathname.startsWith('/onboarding/')) return 'onboarding'
  return 'company'
}

/**
 * Where Continue goes: the page the user opened (`next`) when the chosen
 * persona's home is in the same part of the app or the page is one every
 * persona has (Help & support), otherwise that home. Anyone whose home is an
 * onboarding page (apply, pending, rejected) goes there.
 */
export function entryDestination(home: string, next: string | null): string {
  if (!next) return home
  const area = appAreaOf(home)
  if (area === 'onboarding') return home
  return area === appAreaOf(next) || isSharedPage(next) ? next : home
}

/**
 * Help & support (`/support`, docs/support.md) is every persona's page: shops,
 * couriers and partner desks all ask from there, so a link to it opens it
 * whichever persona is chosen rather than counting as a company page.
 */
function isSharedPage(path: string): boolean {
  const pathname = path.split(/[?#]/)[0]
  return pathname === '/support' || pathname.startsWith('/support/')
}

/** The parts of the app only one kind of option works in, so a link there names it. */
const LINK_PICKS_OPTION = new Set(['platform', 'courier', 'partner'])

/**
 * The option a link chooses on its own, or null: when `next` is a courier,
 * partner or platform page and exactly one option's home is in that part of
 * the app. A FiveStar job link then opens the desk instead of asking "Choose
 * company and role". A company page never chooses — which company is the
 * user's call — and neither does a link two options could serve.
 */
export function optionForNext<T extends { previewRole?: string | null }>(
  options: readonly T[],
  next: string | null,
): T | null {
  if (!next) return null
  const area = appAreaOf(next)
  if (!LINK_PICKS_OPTION.has(area)) return null
  const matching = options.filter((option) => appAreaOf(personaHomePath(option.previewRole)) === area)
  return matching.length === 1 ? matching[0] : null
}

export function performBackofficeLogout(signOut: () => void | Promise<any>): void {
  // Nothing writes COMFORTHUB_SESSION any more, but a tab left open on an older
  // build still can, so logout keeps clearing it (and the old Auth0 keys).
  clearLegacyAuthStorage()
  clearEntryContext()
  void signOut()
}
